<template>
  <div class="quantum-tunneling-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>T ≈ exp(-2kL) | 穿越势垒$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Potential Barrier -->
          <rect x="180" y="60" :width="barrierWidth" height="140" fill="rgba(255,255,255,0.1)" stroke="#888" />
          <text x="185" y="55" fill="#888" font-size="8">势垒 (V > E)</text>
          
          <!-- Incident Wave -->
          <path :d="incidentPath" fill="none" stroke="#00d2ff" stroke-width="2" />
          
          <!-- Transmitted Wave -->
          <path :d="transmittedPath" fill="none" stroke="#00d2ff" stroke-width="2" :opacity="transmission" />
          
          <!-- Reflected Wave -->
          <path :d="reflectedPath" fill="none" stroke="#ff4757" stroke-width="1.5" opacity="0.6" />

          <!-- Particle view (Probability cloud) -->
          <g v-if="showParticles">
             <circle v-for="p in particles" :key="p.id" :cx="p.x" :cy="p.y" r="2" fill="#00d2ff" :opacity="p.opacity" />
          </g>

          <text x="50" y="220" fill="#00d2ff" font-size="8">入射波</text>
          <text x="320" y="220" fill="#00d2ff" font-size="8">透射波 (隧穿)</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>透射系数 T: {{ (transmission * 100).toFixed(2) }}%</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="row items-center gap-4">
          <div class="control-card glass flex-1">
             <label>势垒宽度</label>
             <el-slider v-model="barrierWidth" :min="5" :max="50" size="mini"></el-slider>
          </div>
          <div class="control-card glass flex-1">
             <label>粒子能量</label>
             <el-slider v-model="energy" :min="10" :max="90" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>量子隧穿是微观粒子表现出的波动性结果。在经典力学中，如果粒子的能量 E 低于势垒高度 V，它将被 100% 反射。但在量子力学中，波函数在势垒内部不会立刻归零，而是呈指数衰减。如果势垒足够薄，波函数就能“渗透”过去，使得粒子在障碍物另一侧出现的概率不为零。这是扫描隧道显微镜（STM）、闪存芯片（NAND）以及恒星核聚变发生的根本原因。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuantumTunnelingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      barrierWidth: 20,
      energy: 40,
      t: 0,
      timer: null,
      showParticles: true,
      particles: []
    }
  },
  computed: {
    transmission() {
       // Mock exponential decay: T = exp(-width * (V-E))
       const V = 100;
       return Math.exp(-this.barrierWidth * (V - this.energy) * 0.01);
    },
    incidentPath() {
       let pts = [];
       for (let x = 0; x <= 180; x += 2) {
          let y = 130 + Math.sin(x*0.2 - this.t*0.2) * 20;
          pts.push(`${x},${y}`);
       }
       return "M " + pts.join(" L ");
    },
    transmittedPath() {
       let startX = 180 + this.barrierWidth;
       let pts = [];
       for (let x = startX; x <= 400; x += 2) {
          let y = 130 + Math.sin(x*0.2 - this.t*0.2) * 20;
          pts.push(`${x},${y}`);
       }
       return "M " + pts.join(" L ");
    },
    reflectedPath() {
       let pts = [];
       for (let x = 0; x <= 180; x += 2) {
          let y = 130 + Math.sin(x*0.2 + this.t*0.1) * 10 * (1 - this.transmission);
          pts.push(`${x},${y}`);
       }
       return "M " + pts.join(" L ");
    }
  },
  methods: {
    animate() {
       this.t++;
       if (this.t % 3 === 0) {
          this.particles.push({ id: Math.random(), x: 0, y: 130 + (Math.random()-0.5)*20, opacity: 1 });
       }
       this.particles.forEach((p, idx) => {
          p.x += 4;
          // When hitting barrier
          if (p.x >= 180 && p.x < 180 + this.barrierWidth) {
             if (Math.random() > this.transmission * 0.1) {
                p.x = -100; // Reflected (simplified)
             }
          }
          if (p.x > 400) this.particles.splice(idx, 1);
       });
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.quantum-tunneling-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
